import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { subscribeTable } from '../lib/realtime';
import type { Company } from '../lib/types';

interface UseCompaniesResult {
  companies: Company[];
  loading: boolean;
  error: string | null;
  createCompany: (name: string) => Promise<{ data: Company | null; error: string | null }>;
  toggleStatus: (id: string, current: Company['status']) => Promise<{ error: string | null }>;
  refetch: () => void;
}

export function useCompanies(): UseCompaniesResult {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from('companies')
      .select('*')
      .order('created_at', { ascending: false });
    if (err) { setError(err.message); } else { setCompanies((data as Company[]) ?? []); }
    setLoading(false);
  }, []);

  useEffect(() => {
    refetch();
    const unsub = subscribeTable('companies', refetch);
    return unsub;
  }, [refetch]);

  useEffect(() => {
    if (tick === 0) return;
    refetch();
  }, [tick, refetch]);

  async function createCompany(name: string) {
    const { data, error: err } = await supabase
      .from('companies')
      .insert({ name })
      .select('*')
      .single();
    if (!err) setTick((t) => t + 1);
    return { data: (data as Company | null), error: err?.message ?? null };
  }

  async function toggleStatus(id: string, current: Company['status']) {
    const status: Company['status'] = current === 'active' ? 'suspended' : 'active';
    const { error: err } = await supabase.from('companies').update({ status }).eq('id', id);
    if (!err) setTick((t) => t + 1);
    return { error: err?.message ?? null };
  }

  return { companies, loading, error, createCompany, toggleStatus, refetch: () => setTick((t) => t + 1) };
}
